import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	CircularProgress,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	getVatReportsRequest,
	newIOSSVATReportRequest,
	newIOSSVATReportWithPaymentRequest,
	newNonUnionVATReportRequest,
	newNonUnionVATReportWithPaymentRequest,
	newNonUnionVATXmlReportRequest,
	newNonUnionVATXmlReportWithPaymentRequest,
	newUnionVATReportRequest,
	newUnionVATReportWithPaymentRequest,
	vatReportFileRequest,
	vatReportStatusRequest,
	swissPostVATReportRequest,
	euDdpVATReportRequest,
} from 'redux/reducers/vatreport/reducer';
import {
	getSwissPostVATReport,
	getSwissPostVATReportLoader,
	getVatReports,
	getVatReportsLoader,
	newVatReport,
	vatReportStatus,
	vatReportStatusIsLoad,
} from 'redux/reducers/vatreport/selectors';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { DateTime } from 'luxon';
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import { ROLES } from 'routes/constants';
import { LinearProgressWithLabel } from '../LinearProgressWithLabel';
import { VATReportCard } from './VATReportCard';
import { COLORS } from '../../../services/constants/colors';
import { EasTableValue } from '../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../ui/EasTable/types';
import { IS_NEMAN } from '../../../services/constants/env';

export const VATReportDownload: FC = () => {
	const { taskId } = useParams<{ taskId: string }>();
	const navigate = useNavigate();

	const dispatch = useDispatch();
	const newVatReportTaskId = useSelector(newVatReport);
	const downloadStatus = useSelector(vatReportStatus);

	const downloadStatusIsLoad = useSelector(vatReportStatusIsLoad);

	const vatReports = useSelector(getVatReports);
	const vatReportsIsLoad = useSelector(getVatReportsLoader);

	const swissPostVATReport = useSelector(getSwissPostVATReport);
	const swissPostVATReportIsLoad = useSelector(getSwissPostVATReportLoader);

	const [startDownload, setStartDownload] = useState(!(taskId === 'new'));
	const date = useMemo(
		() => (downloadStatusIsLoad ? (downloadStatus?.timestamp ?? '') : null),
		[downloadStatusIsLoad, downloadStatus?.timestamp],
	);
	const [endDownload, setEndDownload] = useState(false);

	const downloadHandler = () => {
		setEndDownload(false);
		dispatch(newIOSSVATReportRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerWithPayment = () => {
		setEndDownload(false);
		dispatch(newIOSSVATReportWithPaymentRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerNonUnion = () => {
		setEndDownload(false);
		dispatch(newNonUnionVATReportRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerNonUnionWithPayment = () => {
		setEndDownload(false);
		dispatch(newNonUnionVATReportWithPaymentRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerNonUnionXml = () => {
		setEndDownload(false);
		dispatch(newNonUnionVATXmlReportRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerNonUnionXmlWithPayment = () => {
		setEndDownload(false);
		dispatch(newNonUnionVATXmlReportWithPaymentRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerUnion = () => {
		setEndDownload(false);
		dispatch(newUnionVATReportRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerUnionWithPayment = () => {
		setEndDownload(false);
		dispatch(newUnionVATReportWithPaymentRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerSwissPost = () => {
		setEndDownload(false);
		dispatch(swissPostVATReportRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};

	const downloadHandlerEuDdp = () => {
		setEndDownload(false);
		dispatch(euDdpVATReportRequest());
		setStartDownload(true);
		dispatch(getVatReportsRequest());
	};
	useEffect(() => {
		let interval: ReturnType<typeof setInterval>;
		if (startDownload && taskId && taskId !== 'new') {
			interval = setInterval(
				() => {
					dispatch(vatReportStatusRequest({ taskId }));
				},
				startDownload ? 2000 : 0,
			);
		}
		return () => clearInterval(interval);
	}, [dispatch, startDownload, taskId]);

	useEffect(() => {
		dispatch(getVatReportsRequest());
		const interval = setInterval(() => {
			dispatch(getVatReportsRequest());
		}, 5_000);
		return () => clearInterval(interval);
	}, [dispatch]);

	if (downloadStatus?.status === 'finished' && startDownload && taskId) {
		setStartDownload(false);
		dispatch(vatReportFileRequest({ taskId }));
		setEndDownload(true);
	}
	useEffect(() => {
		if (newVatReportTaskId?.taskId) {
			navigate(`/${ROLES.VAT_REPORTS}/${newVatReportTaskId.taskId}`);
		}
	}, [navigate, newVatReportTaskId?.taskId]);

	return (
		<>
			<Paper
				sx={{
					margin: '0px 10px 0 10px',
					maxWidth: IS_NEMAN ? '300px' : 'calc(100vw - 20px)',
					overflowX: 'auto',
				}}
			>
				<Box sx={{ width: 'auto', minWidth: IS_NEMAN ? '300px' : '1200px' }}>
					<Box sx={{ borderRadius: '12px' }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 1,
								width: '100%',
								// width: 'auto',
							}}
						>
							<VATReportCard
								title="IOSS VAT Reports"
								downloadHandler={downloadHandler}
								startDownload={startDownload}
								downloadHandlerWithPayment={downloadHandlerWithPayment}
							/>
							{!IS_NEMAN && (
								<>
									<Divider
										orientation="vertical"
										flexItem
										sx={{ borderColor: 'primary.light', mt: 1, mb: 1 }}
									/>
									<VATReportCard
										title="Union VAT Reports"
										downloadHandler={downloadHandlerUnion}
										startDownload={startDownload}
										downloadHandlerWithPayment={downloadHandlerUnionWithPayment}
									/>
									<Divider
										orientation="vertical"
										flexItem
										sx={{ borderColor: 'primary.light', mt: 1, mb: 1 }}
									/>

									<VATReportCard
										title="Non Union VAT Reports Ireland"
										downloadHandler={downloadHandlerNonUnion}
										startDownload={startDownload}
										downloadHandlerWithPayment={downloadHandlerNonUnionWithPayment}
									/>
									<Divider
										orientation="vertical"
										flexItem
										sx={{ borderColor: 'primary.light', mt: 1, mb: 1 }}
									/>

									<VATReportCard
										title="Non Union VAT Reports Estonia"
										downloadHandler={downloadHandlerNonUnionXml}
										startDownload={startDownload}
										downloadHandlerWithPayment={downloadHandlerNonUnionXmlWithPayment}
									/>
									<Divider
										orientation="vertical"
										flexItem
										sx={{ borderColor: 'primary.light', mt: 1, mb: 1 }}
									/>
									<VATReportCard
										title="SwissPost VAT report"
										downloadHandler={downloadHandlerSwissPost}
										startDownload={startDownload}
									/>
									<Divider
										orientation="vertical"
										flexItem
										sx={{ borderColor: 'primary.light', mt: 1, mb: 1 }}
									/>
									<VATReportCard
										title="EU DDP VAT report"
										downloadHandler={downloadHandlerEuDdp}
										startDownload={startDownload}
									/>
								</>
							)}{' '}
						</Box>
					</Box>
				</Box>
			</Paper>
			<Box
				sx={{
					m: 2,
				}}
			>
				{startDownload && (
					<Box sx={{ width: '100%' }}>
						<LinearProgressWithLabel value={downloadStatus?.progress ?? 0} />
					</Box>
				)}
				{date && (
					<Typography sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>
						Fiscal report date : {date.slice(0, 10)}
					</Typography>
				)}

				{endDownload && (
					<Typography sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>
						File downloaded successfully!
					</Typography>
				)}
			</Box>
			<Paper
				sx={{
					margin: '10px 10px 0 10px',
					maxWidth: 'calc(100vw - 20px)',
					overflowX: 'auto',
				}}
			>
				<Box sx={{ width: 'auto', minWidth: '1200px' }}>
					<Box sx={{ borderRadius: '12px' }}>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								backgroundColor: COLORS.BACKGROUND_SECONDARY,
								p: 0.5,
							}}
						>
							<Typography variant="body1" color="primary" fontWeight="bold">
								VAT Reports
							</Typography>
						</Box>
						<TableContainer>
							<Table sx={{ minWidth: 650 }} aria-label="simple table">
								<TableHead>
									<StyledTableRow
										sx={{
											color: '#fff',
											'& th': { backgroundColor: 'primary.main', color: '#fff' },
											maxHeight: '30px',
											'& .MuiTableCell-root': { p: '3px 5px' },
										}}
									>
										<TableCell>Date of report</TableCell>
										<TableCell>Scheme</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Started at</TableCell>
										<TableCell>Ended at</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Progress</TableCell>
									</StyledTableRow>
								</TableHead>
								<TableBody>
									{vatReports &&
										vatReports.length > 0 &&
										vatReports.map((report) => (
											<StyledTableRow
												key={report.id}
												sx={{
													'& td': { color: COLORS.font_main },
													cursor: report.status === 'finished' ? 'pointer' : 'default',
												}}
												onClick={() => {
													if (report.status === 'finished') {
														dispatch(vatReportFileRequest({ taskId: report.id }));
														dispatch(getVatReportsRequest());
													}
												}}
											>
												<StyledTableCell>
													<EasTableValue
														value={report.timestamp}
														type={ETableStructureType.DATE}
														size="s"
													/>
													{/* {DateTime.fromISO(report.timestamp).toFormat('yyyy.MM.dd ')} */}
												</StyledTableCell>
												<StyledTableCell>
													{/* {report.schemeType.replaceAll('_', ' ').toUpperCase()} */}
													<EasTableValue
														value={report.schemeType.replaceAll('_', ' ').toUpperCase()}
														type={ETableStructureType.STATUS}
														size="s"
													/>
												</StyledTableCell>
												<StyledTableCell>
													<EasTableValue
														value={report.type.replaceAll('_', ' ')}
														type={ETableStructureType.STATUS}
														size="s"
													/>
												</StyledTableCell>
												<StyledTableCell>
													<EasTableValue
														value={report.startedAt}
														type={ETableStructureType.DATE_TIME}
														size="s"
													/>
													{/* {DateTime.fromISO(report.startedAt).toFormat('yyyy.MM.dd HH:mm:ss')} */}
												</StyledTableCell>
												<StyledTableCell>
													<EasTableValue
														value={report.endedAt}
														type={ETableStructureType.DATE_TIME}
														size="s"
													/>
													{/* {report.endedAt &&
														DateTime.fromISO(report.endedAt).toFormat('yyyy.MM.dd HH:mm:ss')} */}
												</StyledTableCell>
												<StyledTableCell sx={{ verticalAlign: 'center' }}>
													<EasTableValue
														value={report.status}
														type={ETableStructureType.STATUS}
														size="s"
														tableStructureItem={{
															colors: [
																['finished', 'success.light'],
																['pending', 'warning.light'],
																['working', 'info.light'],
																['error', 'error.light'],
															],
														}}
													/>
													{/* <Chip
														label={report.status ?? 'Unknown'}
														size="small"
														sx={{
															p: '0 5px',
															fontSize: '.7rem',
															height: '20px',
															bgcolor:
																report.status === 'finished'
																	? 'success.light'
																	: // : report.status ===
																		//   SwedBankIntegrationStatus.TO_BE_MANUALLY_PROCESSED_STATUS
																		// ? 'warning.light'
																		// : report.status ===
																		//   SwedBankIntegrationStatus.INVOICE_NOT_FOUND_STATUS
																		// ? 'info.light'
																		'warning.light',
															color: '#00000090',
														}}
													/> */}
													{/* <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '12px' }}>
															<CircularProgress
																variant="determinate"
																value={report.progress}
																size="14px"
																sx={{
																	opacity: report.status === 'finished' ? 0 : 1,
																	marginLeft: '12px',
																}}
															/>
															{report.progress < 100 && (
																<Typography sx={{ fontSize: '.7rem', marginLeft: '12px' }}>
																	{report.progress} %
																</Typography>
															)}
														</Box> */}
												</StyledTableCell>
												<StyledTableCell sx={{ width: '300px' }}>
													{report.status !== 'finished' && (
														<LinearProgressWithLabel
															value={report.progress}
															sx={{
																height: '7px',
																borderRadius: '10px',
																width: '100%',
																fontSize: '.6rem',
															}}
														/>
													)}
												</StyledTableCell>
											</StyledTableRow>
										))}
									{(!vatReports || vatReports.length === 0) && (
										<StyledTableRow sx={{ width: '100%' }}>
											<StyledTableCell colSpan={7}>
												<Typography
													sx={{
														color: COLORS.PRIMARY,
														fontWeight: 600,
														fontSize: '1.5rem',
														p: 2,
														textAlign: 'center',
													}}
												>
													No reports found
												</Typography>
											</StyledTableCell>
										</StyledTableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				</Box>
			</Paper>
		</>
	);
};
